import React, { useState, useEffect } from "react";

const Hero = () => {
  const [currentRole, setCurrentRole] = useState(0);
  const roles = [
    "English Language Expert",
    "Curriculum Designer",
    "Educational Consultant",
    "Online Tutor",
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentRole((prev) => (prev + 1) % roles.length);
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) element.scrollIntoView({ behavior: "smooth" });
  };

  const socialLinks = [
    {
      name: "Facebook",
      icon: "/images/fb-thumb.png",
      href: "#",
      color: "hover:bg-blue-600",
    },
    {
      name: "GitHub",
      icon: "/images/github-thumb.png",
      href: "#",
      color: "hover:bg-gray-800",
    },
    {
      name: "YouTube",
      icon: "/images/youtube-thumb.png",
      href: "#",
      color: "hover:bg-red-600",
    },
    {
      name: "Instagram",
      icon: "/images/instagram-thumb.png",
      href: "#",
      color: "hover:bg-pink-600",
    },
  ];

  const stats = [
    { number: "5+", label: "Years Experience" },
    { number: "1200+", label: "Students Taught" },
    { number: "98%", label: "Success Rate" },
  ];

  return (
    <section
      id="home"
      className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 dark:from-gray-900 dark:via-gray-800 dark:to-indigo-900 flex items-center pt-20 animate-gradient"
    >
      <div className="max-w-7xl mx-auto px-4 py-20">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          {/* Content */}
          <div className="text-center lg:text-left space-y-8">
            <div className="space-y-6">
              <div data-aos="fade-right">
                <span className="inline-block bg-gradient-to-r from-indigo-100 to-purple-100 dark:from-indigo-900 dark:to-purple-900 text-indigo-800 dark:text-indigo-200 px-4 py-2 rounded-full text-sm font-semibold mb-4">
                  🎓 Professional English Educator
                </span>
              </div>

              <h1
                className="text-5xl lg:text-7xl font-bold font-display leading-tight"
                data-aos="fade-up"
                data-aos-delay="200"
              >
                <span className="text-gray-800 dark:text-white">
                  Hello, I'm
                </span>
                <br />
                <span className="gradient-text">Rashida Akter</span>
              </h1>

              <div
                className="text-2xl lg:text-3xl font-semibold text-gray-600 dark:text-gray-300 h-16 flex items-center"
                data-aos="fade-left"
                data-aos-delay="400"
              >
                <span className="text-indigo-600 dark:text-blue-400">
                  {roles[currentRole]}
                </span>
              </div>
            </div>

            <p
              className="text-lg text-gray-600 dark:text-gray-300 max-w-2xl leading-relaxed font-bangla"
              data-aos="fade-up"
              data-aos-delay="600"
            >
              আমি একজন অভিজ্ঞ ইংরেজি শিক্ষক যিনি গত ৫ বছর ধরে শিক্ষার্থীদের
              ইংরেজিতে দক্ষ করে তুলছি। আমার লক্ষ্য হল প্রতিটি শিক্ষার্থীকে
              আত্মবিশ্বাসী এবং সাবলীল ইংরেজি স্পিকার হিসেবে গড়ে তোলা।
            </p>

            {/* Stats */}
            <div
              className="grid grid-cols-3 gap-8 py-8"
              data-aos="fade-up"
              data-aos-delay="800"
            >
              {stats.map((stat, index) => (
                <div
                  key={index}
                  className="text-center"
                  data-aos="zoom-in"
                  data-aos-delay={1000 + index * 100}
                >
                  <div className="text-4xl font-bold font-display gradient-text">
                    {stat.number}
                  </div>
                  <div className="text-sm text-gray-600 font-medium">
                    {stat.label}
                  </div>
                </div>
              ))}
            </div>

            {/* CTA Buttons */}
            <div
              className="flex flex-col sm:flex-row gap-4"
              data-aos="fade-up"
              data-aos-delay="1300"
            >
              <button
                onClick={() => scrollToSection("lessons")}
                className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white px-8 py-4 rounded-full font-semibold hover:from-indigo-700 hover:to-purple-700 transform hover:scale-105 transition-all duration-200 shadow-xl"
              >
                Start Learning Journey
              </button>
              <button
                onClick={() => scrollToSection("portfolio")}
                className="border-2 border-indigo-600 text-indigo-600 px-8 py-4 rounded-full font-semibold hover:bg-indigo-600 hover:text-white transition-all duration-200"
              >
                View My Work
              </button>
            </div>

            {/* Social Links */}
            <div
              className="flex justify-center lg:justify-start space-x-6"
              data-aos="fade-right"
              data-aos-delay="1500"
            >
              {socialLinks.map((social, index) => (
                <a
                  key={index}
                  href={social.href}
                  className={`w-12 h-12 bg-gray-100 text-gray-600 rounded-full flex items-center justify-center ${social.color} hover:text-white transition-all transform hover:scale-110`}
                  data-aos="zoom-in"
                  data-aos-delay={1600 + index * 100}
                  title={social.name}
                >
                  <img
                    src={social.icon}
                    alt={social.name}
                    className="w-6 h-6"
                  />
                </a>
              ))}
            </div>
          </div>

          {/* Hero Image */}
          <div
            className="flex justify-center lg:justify-end"
            data-aos="fade-left"
            data-aos-delay="700"
          >
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-r from-indigo-400 to-purple-500 rounded-full blur-3xl opacity-20 animate-pulse"></div>
              <div className="relative bg-white rounded-3xl p-8 shadow-2xl">
                <img
                  src="/images/hero-student.png"
                  alt="English Teacher Rashida Akter"
                  className="w-full max-w-md mx-auto drop-shadow-lg hover:scale-105 transition-transform duration-300"
                />
                <div
                  className="absolute -top-4 -right-4 bg-gradient-to-r from-green-500 to-emerald-500 text-white px-4 py-2 rounded-full text-sm font-semibold shadow-lg animate-pulse"
                  data-aos="bounce"
                  data-aos-delay="2000"
                >
                  ✨ Available for Teaching
                </div>
                <div
                  className="absolute -bottom-4 -left-4 bg-gradient-to-r from-blue-500 to-indigo-500 text-white px-4 py-2 rounded-full text-sm font-semibold shadow-lg"
                  data-aos="bounce"
                  data-aos-delay="2200"
                >
                  🏆 Top Rated Tutor
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
